/**
 * Free Sample Button Handler
 * Manages the free sample add-to-cart flow with validation and feedback
 */

const FREE_SAMPLE_LIMIT = 25;
const STANDARD_SAMPLE_PRICE = '$3';
const FREE_SAMPLE_BUTTON_LABEL = 'Order a free sample';
const AUTH_REQUIRED_MESSAGE = 'To order a sample you must sign in or create an account';

class FreeSampleButton extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('.free-sample-form');
    this.button = this.querySelector('.free-sample-button');
    this.authModal = this.querySelector('.free-sample-auth-modal');
    this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    this.variantMapping = this.parseVariantMapping();
    this.sampleVariantLookup = this.buildSampleVariantLookup();
    this.sampleVariantIdCache = new Map();
    this.variantInput = null;
    this.variantChangeUnsubscriber = null;
    this.cartUpdateUnsubscriber = null;
    this.boundVariantInputChange = null;
    this.boundAuthModalClick = null;
    this.boundAuthModalKeydown = null;
    this.authModalOriginalParent = null;
    this.authModalOriginalNextSibling = null;
    this.guestButtonUpdateTimestamp = 0;
    this.apiUrl = 'https://sample-validation-api.vercel.app';
    this.apiKey = 'urban_district_api';
    console.log('[FREE-SAMPLE] FreeSampleButton constructor called');
    console.log('[FREE-SAMPLE]   - form found:', !!this.form);
    console.log('[FREE-SAMPLE]   - button found:', !!this.button);
  }

  getDefaultSessionData() {
    return {
      has_exceeded_limit: false,
      samples_this_week: 0,
      free_samples_remaining: FREE_SAMPLE_LIMIT,
      paid_samples_required: 0,
      next_reset_date: null,
      pricing_tier: 'free',
      free_sample_limit: FREE_SAMPLE_LIMIT,
      standard_sample_price: STANDARD_SAMPLE_PRICE,
      entitlement_source: 'default'
    };
  }

  normalizeSessionData(sessionData) {
    const normalizedSessionData = {
      ...this.getDefaultSessionData(),
      ...(sessionData || {})
    };

    const freeSampleLimit = Number(normalizedSessionData.free_sample_limit) || FREE_SAMPLE_LIMIT;

    normalizedSessionData.free_sample_limit = freeSampleLimit;
    normalizedSessionData.samples_this_week = Math.max(0, Number(normalizedSessionData.samples_this_week) || 0);
    normalizedSessionData.paid_samples_required = Math.max(0, Number(normalizedSessionData.paid_samples_required) || 0);
    normalizedSessionData.standard_sample_price = normalizedSessionData.standard_sample_price || STANDARD_SAMPLE_PRICE;

    if (typeof normalizedSessionData.free_samples_remaining !== 'number') {
      normalizedSessionData.free_samples_remaining = normalizedSessionData.has_exceeded_limit ? 0 : freeSampleLimit;
    }

    normalizedSessionData.free_samples_remaining = Math.max(0, Number(normalizedSessionData.free_samples_remaining) || 0);
    normalizedSessionData.has_exceeded_limit = normalizedSessionData.free_samples_remaining === 0;
    normalizedSessionData.pricing_tier = normalizedSessionData.has_exceeded_limit ? 'paid' : 'free';

    return normalizedSessionData;
  }

  async getFreeSampleCartQuantity() {
    try {
      const response = await fetch('/cart.js', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        throw new Error(`Cart request failed: ${response.status}`);
      }

      const cart = await response.json();
      const items = Array.isArray(cart?.items) ? cart.items : [];

      return items.reduce((sum, item) => {
        const isFreeSample = item?.properties?._sample_order === 'true' && item?.properties?._price_tier === 'free';
        return isFreeSample ? sum + (Number(item.quantity) || 0) : sum;
      }, 0);
    } catch (error) {
      console.warn('[FREE-SAMPLE] Failed to fetch cart free-sample quantity', error);
      return 0;
    }
  }

  buildEffectiveSessionData(sessionData, cartFreeSampleQuantity = 0) {
    const normalizedSessionData = this.normalizeSessionData(sessionData);
    const freeSampleLimit = normalizedSessionData.free_sample_limit || FREE_SAMPLE_LIMIT;
    const confirmedSamplesThisWeek = Math.max(0, Number(normalizedSessionData.samples_this_week) || 0);
    const effectiveCartFreeSampleQuantity = Math.max(0, Number(cartFreeSampleQuantity) || 0);
    const samplesThisWeek = confirmedSamplesThisWeek + effectiveCartFreeSampleQuantity;
    const freeSamplesRemaining = Math.max(0, freeSampleLimit - samplesThisWeek);

    return this.normalizeSessionData({
      ...normalizedSessionData,
      confirmed_samples_this_week: confirmedSamplesThisWeek,
      cart_free_samples_in_cart: effectiveCartFreeSampleQuantity,
      samples_this_week: samplesThisWeek,
      free_samples_remaining: freeSamplesRemaining,
      paid_samples_required: Math.max(0, normalizedSessionData.paid_samples_required || 0),
      has_exceeded_limit: freeSamplesRemaining === 0,
      pricing_tier: freeSamplesRemaining === 0 ? 'paid' : 'free'
    });
  }

  getButtonLabel(sessionData, hasCustomer) {
    const normalizedSessionData = this.normalizeSessionData(sessionData);

    // Show paid label when free samples are exhausted (for both customers and guests)
    if (normalizedSessionData.free_samples_remaining === 0) {
      return `Order - ${normalizedSessionData.standard_sample_price} Sample`;
    }

    // Show free label if free samples are still available
    return FREE_SAMPLE_BUTTON_LABEL;
  }

  normalizeVariantId(rawValue) {
    if (rawValue == null) {
      return '';
    }

    const trimmedValue = String(rawValue).trim();

    if (!trimmedValue) {
      return '';
    }

    if (/^\d+$/.test(trimmedValue)) {
      return trimmedValue;
    }

    const gidMatch = trimmedValue.match(/gid:\/\/shopify\/ProductVariant\/(\d+)/i);
    if (gidMatch) {
      return gidMatch[1];
    }

    const trailingDigitsMatch = trimmedValue.match(/(\d+)$/);
    if (trailingDigitsMatch) {
      return trailingDigitsMatch[1];
    }

    return trimmedValue;
  }

  normalizeSku(rawValue) {
    if (rawValue == null) {
      return '';
    }

    return String(rawValue).trim().toUpperCase();
  }

  async getCartAddErrorMessage(response) {
    let responsePayload = null;

    try {
      responsePayload = await response.clone().json();
    } catch (jsonError) {
      try {
        responsePayload = await response.clone().text();
      } catch (textError) {
        responsePayload = null;
      }
    }

    console.error('[FREE-SAMPLE]   /cart/add response payload:', responsePayload);

    if (responsePayload && typeof responsePayload === 'object') {
      return responsePayload.description || responsePayload.message || responsePayload.status || 'Failed to add sample to cart';
    }

    if (typeof responsePayload === 'string' && responsePayload.trim()) {
      return responsePayload.trim();
    }

    return 'Failed to add sample to cart';
  }

  getStatusCopy(sessionData, hasCustomer) {
    const normalizedSessionData = this.normalizeSessionData(sessionData);
    const unitLabel = normalizedSessionData.free_samples_remaining === 1 ? 'unit' : 'units';
    const additionalSampleSuffix = `Additional sample: ${normalizedSessionData.standard_sample_price} each`;

    // Show limit reached message when free samples are exhausted (for both customers and guests)
    if (normalizedSessionData.free_samples_remaining === 0) {
      return `Your current free sample allowance has been used.\n${additionalSampleSuffix}.`;
    }

    // For guests with available samples, show login prompt
    if (!hasCustomer) {
      return `Log in to unlock up to ${normalizedSessionData.free_sample_limit} free sample units every 7 days.\n${additionalSampleSuffix}.`;
    }

    // For logged-in customers, show remaining count
    return `${normalizedSessionData.free_samples_remaining} of ${normalizedSessionData.free_sample_limit} free sample ${unitLabel} left in your current 7-day window.\n${additionalSampleSuffix}.`;
  }

  initializeAuthModal() {
    if (!this.authModal) {
      return;
    }

    // Keep modal outside transformed/card containers so it is viewport-centered.
    if (this.authModal.parentElement !== document.body) {
      this.authModalOriginalParent = this.authModal.parentElement;
      this.authModalOriginalNextSibling = this.authModal.nextSibling;
      document.body.appendChild(this.authModal);
    }

    this.boundAuthModalClick = (event) => {
      const clickedOverlay = event.target.classList.contains('free-sample-auth-modal');
      const clickedCloseButton = Boolean(event.target.closest('[data-free-sample-auth-close]'));

      if (clickedOverlay || clickedCloseButton) {
        this.hideAuthPrompt();
      }
    };

    this.boundAuthModalKeydown = (event) => {
      if (event.key === 'Escape' && !this.authModal.hidden) {
        event.preventDefault();
        this.hideAuthPrompt();
      }
    };

    this.authModal.addEventListener('click', this.boundAuthModalClick);
    document.addEventListener('keydown', this.boundAuthModalKeydown);
  }

  showAuthPrompt() {
    if (!this.authModal) {
      return;
    }

    this.authModal.hidden = false;
    this.classList.add('free-sample-button-wrapper--auth-modal-visible');
    document.body.classList.add('free-sample-auth-modal-open');

    const firstActionLink = this.authModal.querySelector('[data-free-sample-auth-primary]');

    if (firstActionLink) {
      firstActionLink.focus({ preventScroll: true });
    }
  }

  hideAuthPrompt() {
    if (!this.authModal) {
      return;
    }

    this.authModal.hidden = true;
    this.classList.remove('free-sample-button-wrapper--auth-modal-visible');

    if (!document.querySelector('.free-sample-auth-modal:not([hidden])')) {
      document.body.classList.remove('free-sample-auth-modal-open');
    }
  }

  connectedCallback() {
    console.log('[FREE-SAMPLE] <free-sample-button> element connected');
    if (!this.form || !this.button) {
      console.warn('[FREE-SAMPLE] Form or button not found in template');
      return;
    }

    this.form.addEventListener('submit', (e) => this.handleSubmit(e));
    this.initializeVariantSync();
    this.initializeCartSync();
    this.initializeAuthModal();

    const isButtonOnlyWrapper = this.dataset.buttonOnly === 'true';

    if (isButtonOnlyWrapper) {
      const email = this.getCustomerEmail();
      
      if (email) {
        // For logged-in users with cached session data, use that
        const cachedSessionData = this.getCachedSessionData(email);
        if (cachedSessionData) {
          this.updateAllButtons(cachedSessionData, true);
        }
      } else {
        // For guests, use cached guest session data or fetch it
        this.getOrFetchGuestSessionData().then((guestSessionData) => {
          this.updateAllButtons(guestSessionData, false);
        }).catch((error) => {
          console.warn('[FREE-SAMPLE] Error fetching guest session data for button-only wrapper:', error);
          this.updateAllButtons(this.getDefaultSessionData(), false);
        });
      }

      return;
    }
    
    // Initialize session validation on page load
    // This validates the customer and updates button states before user interaction
    console.log('[FREE-SAMPLE] Scheduling initializeSessionValidation in 100ms');
    setTimeout(() => this.initializeSessionValidation(), 100);
  }

  disconnectedCallback() {
    if (this.boundVariantInputChange && this.variantInput) {
      this.variantInput.removeEventListener('change', this.boundVariantInputChange);
    }

    if (this.variantChangeUnsubscriber) {
      this.variantChangeUnsubscriber();
      this.variantChangeUnsubscriber = null;
    }

    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
      this.cartUpdateUnsubscriber = null;
    }

    if (this.authModal && this.boundAuthModalClick) {
      this.authModal.removeEventListener('click', this.boundAuthModalClick);
      this.boundAuthModalClick = null;
    }

    if (this.boundAuthModalKeydown) {
      document.removeEventListener('keydown', this.boundAuthModalKeydown);
      this.boundAuthModalKeydown = null;
    }

    if (this.authModal && this.authModalOriginalParent) {
      if (this.authModalOriginalNextSibling && this.authModalOriginalNextSibling.parentNode === this.authModalOriginalParent) {
        this.authModalOriginalParent.insertBefore(this.authModal, this.authModalOriginalNextSibling);
      } else {
        this.authModalOriginalParent.appendChild(this.authModal);
      }

      this.authModalOriginalParent = null;
      this.authModalOriginalNextSibling = null;
    }

    if (!document.querySelector('.free-sample-auth-modal:not([hidden])')) {
      document.body.classList.remove('free-sample-auth-modal-open');
    }
  }

  parseVariantMapping() {
    const mappingElement = this.querySelector('.free-sample-variant-mapping');

    if (!mappingElement) {
      return new Map();
    }

    try {
      const parsedMapping = JSON.parse(mappingElement.textContent || '[]');
      return new Map(
        parsedMapping
          .filter((entry) => entry && entry.variant_id)
          .map((entry) => [this.normalizeVariantId(entry.variant_id), entry])
      );
    } catch (error) {
      console.warn('[FREE-SAMPLE] Failed to parse variant mapping:', error);
      return new Map();
    }
  }

  buildSampleVariantLookup() {
    const sampleVariantLookup = new Map();

    const registerSampleVariant = (sampleSku, sampleVariantId, priceTier) => {
      const normalizedSampleSku = this.normalizeSku(sampleSku);
      const normalizedSampleVariantId = this.normalizeVariantId(sampleVariantId);

      if (!normalizedSampleSku || !normalizedSampleVariantId) {
        return;
      }

      sampleVariantLookup.set(normalizedSampleSku, {
        sku: sampleSku,
        variantId: normalizedSampleVariantId,
        priceTier
      });
    };

    this.variantMapping.forEach((entry) => {
      registerSampleVariant(entry.free_sample_sku, entry.free_sample_variant_id, 'free');
      registerSampleVariant(entry.paid_sample_sku, entry.paid_sample_variant_id, 'paid');
      registerSampleVariant(entry.legacy_sample_sku, entry.legacy_sample_variant_id, 'legacy');
    });

    return sampleVariantLookup;
  }

  getRelatedProductForm() {
    const productFormId = this.dataset.productFormId;

    if (productFormId) {
      return document.getElementById(productFormId);
    }

    return this.closest('product-info')?.querySelector('form[id^="product-form-"]') || null;
  }

  getVariantInput() {
    const productForm = this.getRelatedProductForm();

    return productForm?.querySelector('input[name="id"]') || null;
  }

  getCurrentSelectedVariantId() {
    const variantInput = this.getVariantInput();

    if (!variantInput) {
      return '';
    }

    return this.normalizeVariantId(variantInput.value || variantInput.getAttribute('value') || '');
  }

  getCustomerEmail() {
    if (!this.form) {
      return window.Shopify?.customer?.email || '';
    }

    const formData = new FormData(this.form);
    return formData.get('email') || window.Shopify?.customer?.email || '';
  }

  getConfirmedSamplesThisWeek(sessionData) {
    const confirmedSamples = Number(sessionData?.confirmed_samples_this_week);

    if (Number.isFinite(confirmedSamples) && confirmedSamples >= 0) {
      return confirmedSamples;
    }

    const totalSamples = Number(sessionData?.samples_this_week) || 0;
    const cartSamples = Number(sessionData?.cart_free_samples_in_cart) || 0;
    return Math.max(0, totalSamples - cartSamples);
  }

  applyVariantMappingById(variantId) {
    if (!variantId || !this.form || !this.variantMapping.size) {
      return false;
    }

    const mapping = this.variantMapping.get(this.normalizeVariantId(variantId));

    if (!mapping) {
      return false;
    }

    const setFieldValue = (fieldName, fieldValue) => {
      const field = this.form.querySelector(`[name="${fieldName}"]`);
      if (field) {
        field.value = fieldValue || '';
      }
    };

    setFieldValue('sku', mapping.sku);
    setFieldValue('free-sample-sku', mapping.free_sample_sku);
    setFieldValue('paid-sample-sku', mapping.paid_sample_sku);
    setFieldValue('legacy-sample-sku', mapping.legacy_sample_sku);
    setFieldValue('free-sample-variant-id', mapping.free_sample_variant_id);
    setFieldValue('paid-sample-variant-id', mapping.paid_sample_variant_id);
    setFieldValue('legacy-sample-variant-id', mapping.legacy_sample_variant_id);
    setFieldValue('parent-variant-image-url', mapping.parent_variant_image_url);
    setFieldValue('parent-variant-image-alt', mapping.parent_variant_image_alt);
    setFieldValue('parent-variant-image-aspect-ratio', mapping.parent_variant_image_aspect_ratio);

    return true;
  }

  getSelectedSampleSku({ isPaid, freeSampleSku, paidSampleSku, legacySampleSku }) {
    if (isPaid) {
      return paidSampleSku || legacySampleSku || '';
    }

    return freeSampleSku || legacySampleSku || '';
  }

  resolveSampleVariantId(sampleSku, fallbackVariantId = '') {
    const normalizedSampleSku = this.normalizeSku(sampleSku);

    if (normalizedSampleSku) {
      const matchedSampleVariant = this.sampleVariantLookup.get(normalizedSampleSku);

      if (matchedSampleVariant?.variantId) {
        return matchedSampleVariant.variantId;
      }
    }

    return this.normalizeVariantId(fallbackVariantId);
  }

  async fetchSampleVariantIdBySku(sampleSku) {
    const normalizedSampleSku = this.normalizeSku(sampleSku);

    if (!normalizedSampleSku) {
      return '';
    }

    if (this.sampleVariantIdCache.has(normalizedSampleSku)) {
      return this.sampleVariantIdCache.get(normalizedSampleSku) || '';
    }

    const response = await fetch(`${this.apiUrl}/api/sample-variant-lookup`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.apiKey}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ sku: sampleSku })
    });

    if (!response.ok) {
      throw new Error('Unable to resolve sample SKU to a Shopify variant');
    }

    const payload = await response.json();
    const matchedVariant = Array.isArray(payload?.variants)
      ? payload.variants.find((variant) => this.normalizeSku(variant?.sku) === normalizedSampleSku)
      : null;
    const resolvedVariantId = this.normalizeVariantId(matchedVariant?.variant_id || '');

    this.sampleVariantIdCache.set(normalizedSampleSku, resolvedVariantId);

    return resolvedVariantId;
  }

  async getSampleVariantId(sampleSku, fallbackVariantId = '') {
    const mappedVariantId = this.resolveSampleVariantId(sampleSku, fallbackVariantId);

    if (mappedVariantId) {
      return mappedVariantId;
    }

    return this.fetchSampleVariantIdBySku(sampleSku);
  }

  syncToCurrentVariant() {
    const currentVariantId = this.getCurrentSelectedVariantId();

    if (!currentVariantId) {
      return;
    }

    this.applyVariantMappingById(currentVariantId);
  }

  initializeVariantSync() {
    this.variantInput = this.getVariantInput();
    this.boundVariantInputChange = () => this.syncToCurrentVariant();

    if (this.variantInput) {
      this.variantInput.addEventListener('change', this.boundVariantInputChange);
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const sectionId = this.dataset.sectionId;
        const eventSectionId = event?.data?.sectionId;

        if (sectionId && eventSectionId && sectionId !== eventSectionId) {
          return;
        }

        const currentVariantId = this.normalizeVariantId(event?.data?.variant?.id || '');
        if (currentVariantId) {
          this.applyVariantMappingById(currentVariantId);
        }
      });
    }

    this.syncToCurrentVariant();
  }

  initializeCartSync() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined' || !PUB_SUB_EVENTS.cartUpdate) {
      return;
    }

    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, async () => {
      const email = this.getCustomerEmail();

      if (email) {
        const cachedSessionData = this.getCachedSessionData(email);

        if (!cachedSessionData) {
          return;
        }

        const cartFreeSampleQuantity = await this.getFreeSampleCartQuantity();
        const effectiveSessionData = this.buildEffectiveSessionData({
          ...cachedSessionData,
          samples_this_week: this.getConfirmedSamplesThisWeek(cachedSessionData)
        }, cartFreeSampleQuantity);

        this.setCachedSessionData(email, effectiveSessionData, true);
      } else {
        // For guests, only refresh button UI if we haven't updated recently (debounce)
        // This prevents flickering when cartUpdate event fires immediately after handleSubmit
        const timeSinceLastUpdate = Date.now() - this.guestButtonUpdateTimestamp;
        if (timeSinceLastUpdate > 500) {
          // Enough time has passed, safe to update
          const cartFreeSampleQuantity = await this.getFreeSampleCartQuantity();
          const guestSessionData = this.getDefaultSessionData();
          guestSessionData.free_samples_remaining = Math.max(0, FREE_SAMPLE_LIMIT - cartFreeSampleQuantity);
          guestSessionData.has_exceeded_limit = cartFreeSampleQuantity >= FREE_SAMPLE_LIMIT;
          this.updateAllButtons(guestSessionData, false);
          // Update cached guest session data for page navigations
          if (window.freeSampleGuestSessionData) {
            window.freeSampleGuestSessionData.data = guestSessionData;
            window.freeSampleGuestSessionData.timestamp = Date.now();
          }
        } else {
          // Recent update, skip to avoid flickering
          console.log('[FREE-SAMPLE] Skipping cartUpdate button refresh (too recent, debounced)');
        }
      }
    });
  }

  async initializeSessionValidation() {
    try {
      // Wait for form to be available
      await this.waitForForm();
      
      const formData = new FormData(this.form);
      const email = formData.get('email') || window.Shopify?.customer?.email || '';
      
      console.log('[FREE-SAMPLE] initializeSessionValidation() called');
      console.log('[FREE-SAMPLE] Customer email:', email);
      
      if (!email) {
        console.debug('[FREE-SAMPLE] No customer email found - anonymous/guest user');
        // For guests, use cached or fetch guest session data
        const guestSessionData = await this.getOrFetchGuestSessionData();
        console.log('[FREE-SAMPLE] Guest has', FREE_SAMPLE_LIMIT - guestSessionData.free_samples_remaining, 'free samples in cart, remaining:', guestSessionData.free_samples_remaining);
        this.updateAllButtons(guestSessionData, false);
        return;
      }

      // Store session data globally so all buttons can access it
      if (!window.freeSampleSessionData) {
        window.freeSampleSessionData = {};
      }

      // Call validation endpoint to check 7-day sample limit
      const [sessionData, cartFreeSampleQuantity] = await Promise.all([
        this.validateCustomerSession(email),
        this.getFreeSampleCartQuantity()
      ]);
      const effectiveSessionData = this.buildEffectiveSessionData(sessionData, cartFreeSampleQuantity);
      console.log('[FREE-SAMPLE] Session data received:', sessionData);
      
      // Cache the result in window for all buttons to access
      window.freeSampleSessionData[email] = effectiveSessionData;

      // Update all buttons on the page with the validated state
      console.log('[FREE-SAMPLE] Calling updateAllButtons with freeSamplesRemaining:', window.freeSampleSessionData[email].free_samples_remaining);
      this.updateAllButtons(window.freeSampleSessionData[email], true);

    } catch (error) {
      console.error('[FREE-SAMPLE] Error initializing session validation:', error);
      this.updateAllButtons(this.getDefaultSessionData(), false);
    }
  }

  async validateCustomerSession(email) {
    try {
      console.log('[FREE-SAMPLE] validateCustomerSession() calling API:', `${this.apiUrl}/api/sample-session`);
      const response = await fetch(`${this.apiUrl}/api/sample-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });

      console.log('[FREE-SAMPLE] API response status:', response.status);

      if (!response.ok) {
        console.warn('[FREE-SAMPLE] Session validation response not ok:', response.status);
        return this.getDefaultSessionData();
      }

      const data = await response.json();
      console.log('[FREE-SAMPLE] API returned:', data);
      return data;
    } catch (error) {
      console.error('[FREE-SAMPLE] Session validation error:', error);
      return this.getDefaultSessionData();
    }
  }

  getCachedSessionData(email) {
    if (!email || !window.freeSampleSessionData || !window.freeSampleSessionData[email]) {
      return null;
    }

    return this.normalizeSessionData(window.freeSampleSessionData[email]);
  }

  setCachedSessionData(email, sessionData, hasCustomer = true) {
    if (!email) {
      return this.normalizeSessionData(sessionData);
    }

    if (!window.freeSampleSessionData) {
      window.freeSampleSessionData = {};
    }

    const normalizedSessionData = this.normalizeSessionData(sessionData);
    window.freeSampleSessionData[email] = normalizedSessionData;
    this.updateAllButtons(normalizedSessionData, hasCustomer);

    return normalizedSessionData;
  }

  async getOrFetchGuestSessionData() {
    // Initialize global guest session data cache
    if (!window.freeSampleGuestSessionData) {
      window.freeSampleGuestSessionData = {
        data: null,
        timestamp: 0,
        promise: null
      };
    }

    // If we have fresh cached data (within 100ms), return it immediately
    const now = Date.now();
    if (window.freeSampleGuestSessionData.data && (now - window.freeSampleGuestSessionData.timestamp) < 100) {
      console.log('[FREE-SAMPLE] Using fresh cached guest session data');
      return window.freeSampleGuestSessionData.data;
    }

    // If a fetch is already in progress, wait for it
    if (window.freeSampleGuestSessionData.promise) {
      console.log('[FREE-SAMPLE] Waiting for in-progress guest session data fetch');
      return window.freeSampleGuestSessionData.promise;
    }

    // Fetch cart quantity and cache it
    window.freeSampleGuestSessionData.promise = this.getFreeSampleCartQuantity()
      .then((cartFreeSampleQuantity) => {
        const guestSessionData = this.getDefaultSessionData();
        guestSessionData.free_samples_remaining = Math.max(0, FREE_SAMPLE_LIMIT - cartFreeSampleQuantity);
        guestSessionData.has_exceeded_limit = cartFreeSampleQuantity >= FREE_SAMPLE_LIMIT;
        
        // Cache for future use
        window.freeSampleGuestSessionData.data = guestSessionData;
        window.freeSampleGuestSessionData.timestamp = Date.now();
        window.freeSampleGuestSessionData.promise = null;
        
        console.log('[FREE-SAMPLE] Fetched and cached guest session data: cart has', cartFreeSampleQuantity, 'free samples');
        return guestSessionData;
      })
      .catch((error) => {
        window.freeSampleGuestSessionData.promise = null;
        console.warn('[FREE-SAMPLE] Failed to fetch guest session data:', error);
        throw error;
      });

    return window.freeSampleGuestSessionData.promise;
  }

  mergeSessionData(sessionData, fallbackSessionData = null) {
    const primarySessionData = this.normalizeSessionData(sessionData);

    if (!fallbackSessionData) {
      return primarySessionData;
    }

    const secondarySessionData = this.normalizeSessionData(fallbackSessionData);
    const freeSampleLimit = Math.max(
      primarySessionData.free_sample_limit || 0,
      secondarySessionData.free_sample_limit || 0,
      FREE_SAMPLE_LIMIT
    );
    const samplesThisWeek = Math.max(
      primarySessionData.samples_this_week,
      secondarySessionData.samples_this_week
    );
    const freeSamplesRemaining = Math.min(
      primarySessionData.free_samples_remaining,
      secondarySessionData.free_samples_remaining
    );
    const paidSamplesRequired = Math.max(
      primarySessionData.paid_samples_required,
      secondarySessionData.paid_samples_required,
      Math.max(0, samplesThisWeek - freeSampleLimit)
    );

    return this.normalizeSessionData({
      ...primarySessionData,
      ...secondarySessionData,
      free_sample_limit: freeSampleLimit,
      samples_this_week: samplesThisWeek,
      free_samples_remaining: freeSamplesRemaining,
      paid_samples_required: paidSamplesRequired,
      has_exceeded_limit: freeSamplesRemaining === 0,
      entitlement_source: primarySessionData.entitlement_source || secondarySessionData.entitlement_source || 'default'
    });
  }

  async ensureSessionData(email) {
    const cachedSessionData = this.getCachedSessionData(email);

    if (cachedSessionData) {
      return cachedSessionData;
    }

    const [sessionData, cartFreeSampleQuantity] = await Promise.all([
      this.validateCustomerSession(email),
      this.getFreeSampleCartQuantity()
    ]);
    const freshSessionData = this.buildEffectiveSessionData(sessionData, cartFreeSampleQuantity);
    return this.setCachedSessionData(email, freshSessionData, true);
  }

  updateAllButtons(sessionData, hasCustomer = true) {
    const normalizedSessionData = this.normalizeSessionData(sessionData);
    const isPaidState = normalizedSessionData.free_samples_remaining === 0;
    const buttonLabel = this.getButtonLabel(normalizedSessionData, hasCustomer);
    const statusCopy = this.getStatusCopy(normalizedSessionData, hasCustomer);

    const allWrappers = document.querySelectorAll('free-sample-button');
    console.log('[FREE-SAMPLE] updateAllButtons() found', allWrappers.length, 'sample button wrappers');

    allWrappers.forEach((wrapper, index) => {
      const isButtonOnlyWrapper = wrapper.dataset.buttonOnly === 'true';
      const buttonText = wrapper.querySelector('.free-sample-button__text');
      const statusText = wrapper.querySelector('.free-sample-button__status');
      const tooltipText = wrapper.querySelector('.free-sample-tooltip__text');
      const noticeText = wrapper.querySelector('.free-sample-notice__text');
      const authModal = wrapper.querySelector('.free-sample-auth-modal');

      if (buttonText) {
        console.log(`[FREE-SAMPLE]   Button ${index} before: "${buttonText.textContent}"`);
        buttonText.textContent = buttonLabel;
        buttonText.dataset.isPaid = String(isPaidState);
        buttonText.dataset.hasCustomer = String(hasCustomer);
        console.log(`[FREE-SAMPLE]   Button ${index} after: "${buttonText.textContent}"`);
      }

      if (statusText) {
        statusText.textContent = statusCopy;
        statusText.hidden = isButtonOnlyWrapper ? !isPaidState : false;
      }

      if (tooltipText) {
        tooltipText.textContent = statusCopy;
      }

      if (noticeText) {
        noticeText.textContent = statusCopy;
      }

      if (authModal && hasCustomer) {
        authModal.hidden = true;
        wrapper.classList.remove('free-sample-button-wrapper--auth-modal-visible');
      }
    });

    // Store session data in the hidden form field for later reference
    const sessionDataFields = document.querySelectorAll('#sample-session-data');
    console.log('[FREE-SAMPLE] Found', sessionDataFields.length, 'session data fields');
    sessionDataFields.forEach(field => {
      field.dataset.exceeded = normalizedSessionData.has_exceeded_limit.toString();
      field.dataset.freeSamplesRemaining = normalizedSessionData.free_samples_remaining.toString();
      field.dataset.hasCustomer = hasCustomer.toString();
      field.value = JSON.stringify(normalizedSessionData);
    });
  }

  async waitForForm() {
    // Ensure form is ready before proceeding
    let attempts = 0;
    while (!this.form && attempts < 10) {
      console.log('[FREE-SAMPLE] waitForForm() attempt', attempts + 1, '- form not ready yet');
      await new Promise(resolve => setTimeout(resolve, 50));
      attempts++;
    }
    if (!this.form) {
      console.warn('[FREE-SAMPLE] waitForForm() gave up after 10 attempts');
    } else {
      console.log('[FREE-SAMPLE] waitForForm() form found after', attempts, 'attempts');
    }
  }

  getCart() {
    if (!this.cart || !document.body.contains(this.cart)) {
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    }

    return this.cart;
  }

  getOptimisticSessionData(sessionData, isPaid) {
    const normalizedSessionData = this.normalizeSessionData(sessionData);

    if (isPaid) {
      return {
        ...normalizedSessionData,
        paid_samples_required: normalizedSessionData.paid_samples_required + 1,
      };
    }

    const nextSamplesThisWeek = normalizedSessionData.samples_this_week + 1;
    const nextFreeSamplesRemaining = Math.max(0, normalizedSessionData.free_samples_remaining - 1);

    return this.normalizeSessionData({
      ...normalizedSessionData,
      samples_this_week: nextSamplesThisWeek,
      free_samples_remaining: nextFreeSamplesRemaining,
      paid_samples_required: normalizedSessionData.paid_samples_required,
      has_exceeded_limit: nextFreeSamplesRemaining === 0,
    });
  }

  async refreshSessionState(email, previousSessionData, isPaid) {
    if (!email) {
      return;
    }

    const optimisticSessionData = this.getOptimisticSessionData(previousSessionData, isPaid);
    this.setCachedSessionData(email, optimisticSessionData, true);

    try {
      const refreshedSessionData = this.normalizeSessionData(await this.validateCustomerSession(email));
      const mergedSessionData = this.mergeSessionData(refreshedSessionData, optimisticSessionData);
      this.setCachedSessionData(email, mergedSessionData, true);
    } catch (error) {
      console.warn('[FREE-SAMPLE] Failed to refresh session state after add, using optimistic state', error);
      this.setCachedSessionData(email, optimisticSessionData, true);
    }
  }

  getRenderableCartState(addResponse) {
    if (!addResponse || !addResponse.sections) {
      return addResponse;
    }

    if (Array.isArray(addResponse.items) && addResponse.items.length > 0) {
      return {
        ...addResponse.items[0],
        sections: addResponse.sections,
      };
    }

    return addResponse;
  }

  async handleSubmit(e) {
    e.preventDefault();

    // Set loading state
    this.button.setAttribute('aria-busy', 'true');
    this.button.disabled = true;

    let preSubmitSessionData = null;

    try {
      const formData = new FormData(this.form);
      
      // Get customer email from form or checkout context
      const email = formData.get('email') || window.Shopify?.customer?.email || '';

      this.hideAuthPrompt();
      
      // Get shipping zip from form
      const shippingZip = formData.get('zip') || formData.get('shipping_zip') || '10001';
      
      // Get sample and parent SKUs from form
      const parentSku = formData.get('sku') || '';
      const freeSampleSku = formData.get('free-sample-sku') || '';
      const paidSampleSku = formData.get('paid-sample-sku') || '';
      const legacySampleSku = formData.get('legacy-sample-sku') || '';
      const freeVariantId = this.normalizeVariantId(formData.get('free-sample-variant-id') || '');
      const paidVariantId = this.normalizeVariantId(formData.get('paid-sample-variant-id') || '');
      const legacyVariantId = this.normalizeVariantId(formData.get('legacy-sample-variant-id') || '');
      const parentVariantImageUrl = formData.get('parent-variant-image-url') || '';
      const parentVariantImageAlt = formData.get('parent-variant-image-alt') || '';
      const parentVariantImageAspectRatio = formData.get('parent-variant-image-aspect-ratio') || '';
      let sampleSku = '';
      let variantId = '';
      
      console.log('[FREE-SAMPLE] handleSubmit()');
      console.log('[FREE-SAMPLE]   email:', email);
      console.log('[FREE-SAMPLE]   shippingZip:', shippingZip);
      console.log('[FREE-SAMPLE]   parent sku:', parentSku);
      console.log('[FREE-SAMPLE]   free sample sku:', freeSampleSku);
      console.log('[FREE-SAMPLE]   paid sample sku:', paidSampleSku);
      console.log('[FREE-SAMPLE]   legacy sample sku:', legacySampleSku);
      console.log('[FREE-SAMPLE]   free variant id:', freeVariantId);
      console.log('[FREE-SAMPLE]   paid variant id:', paidVariantId);
      console.log('[FREE-SAMPLE]   legacy variant id:', legacyVariantId);
      
      // Check if customer has exceeded 7-day limit (use cached session data)
      let isPaid = false;
      let pricingModel = freeVariantId && paidVariantId ? 'dual-variant' : 'single-variant-fallback';
      let freeSamplesRemaining = FREE_SAMPLE_LIMIT;
      let sessionData = this.getDefaultSessionData();

      if (email) {
        sessionData = await this.ensureSessionData(email);
      }

      if (email) {
        sessionData = this.getCachedSessionData(email) || sessionData;
        preSubmitSessionData = sessionData;
        console.log('[FREE-SAMPLE]   has_exceeded_limit:', sessionData.has_exceeded_limit);
        console.log('[FREE-SAMPLE]   samples_this_week:', sessionData.samples_this_week);
        console.log('[FREE-SAMPLE]   free_samples_remaining:', sessionData.free_samples_remaining);

        freeSamplesRemaining = sessionData.free_samples_remaining;
        
        if (sessionData.has_exceeded_limit) {
          isPaid = true;
          console.log('[FREE-SAMPLE]   Free allowance exhausted, selecting paid sample variant');
        } else {
          console.log('[FREE-SAMPLE]   Free allowance available, selecting free sample variant');
        }
      } else {
        // For guests, check cart quantity to see if they've hit the 25-sample cap
        const cartFreeSampleQuantity = await this.getFreeSampleCartQuantity();
        console.log('[FREE-SAMPLE]   Guest user - cart has', cartFreeSampleQuantity, 'free samples');
        
        if (cartFreeSampleQuantity >= FREE_SAMPLE_LIMIT) {
          isPaid = true;
          console.log('[FREE-SAMPLE]   Guest has reached 25-sample limit, selecting paid sample variant');
        } else {
          console.log('[FREE-SAMPLE]   Guest can still add free samples (' + cartFreeSampleQuantity + '/' + FREE_SAMPLE_LIMIT + ')');
        }
      }

      sampleSku = this.getSelectedSampleSku({
        isPaid,
        freeSampleSku,
        paidSampleSku,
        legacySampleSku
      });

      variantId = await this.getSampleVariantId(
        sampleSku,
        isPaid ? paidVariantId || legacyVariantId : freeVariantId || legacyVariantId
      );

      if (sampleSku) {
        console.log('[FREE-SAMPLE]   Selected sample sku:', sampleSku);
      } else {
        console.warn('[FREE-SAMPLE]   No sample SKU configured for this variant, falling back to variant ID selection');
      }

      if (!variantId) {
        throw new Error('Sample variant is not configured for this product');
      }

      if (!freeVariantId || !paidVariantId) {
        pricingModel = 'single-variant-fallback';
      }

      console.log('[FREE-SAMPLE]   Final variant to add:', variantId);
      console.log('[FREE-SAMPLE]   isPaid flag:', isPaid);
      console.log('[FREE-SAMPLE]   pricing model:', pricingModel);

      let optimisticSessionData = null;

      if (email) {
        optimisticSessionData = this.getOptimisticSessionData(sessionData, isPaid);
        this.setCachedSessionData(email, optimisticSessionData, true);
      }

      const cart = this.getCart();
      const cartSections = cart && typeof cart.getSectionsToRender === 'function'
        ? cart.getSectionsToRender().map((section) => section.id)
        : [];
      
      // Add sample variant ID to cart
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: 1,
              properties: {
                '_sample_order': 'true',
                '_sample_sku': sampleSku || parentSku,
                '_price_tier': isPaid ? 'paid' : 'free',
                '_parent_variant_image_url': parentVariantImageUrl,
                '_parent_variant_image_alt': parentVariantImageAlt,
                '_parent_variant_image_aspect_ratio': parentVariantImageAspectRatio
              }
            }
          ],
          sections: cartSections,
          sections_url: window.location.pathname
        })
      });

      console.log('[FREE-SAMPLE]   /cart/add response status:', response.status);

      if (!response.ok) {
        const errorMessage = await this.getCartAddErrorMessage(response);
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('[FREE-SAMPLE]   Cart item added:', data.items[0]);

      const renderableCartState = this.getRenderableCartState(data);

      if (cart && renderableCartState?.sections) {
        cart.renderContents(renderableCartState);
      }

      // Publish cart update event so cart sections refresh
      // This tells the theme to update the cart drawer, cart count, etc.
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        console.log('[FREE-SAMPLE]   Publishing cart-update event');
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'free-sample-button',
          productVariantId: variantId,
          cartData: data,
        });
      } else {
        console.warn('[FREE-SAMPLE]   publish or PUB_SUB_EVENTS not available, cart may not update');
      }

      // For guests, immediately update button UI to reflect new cart state
      if (!email) {
        const updatedCartQuantity = await this.getFreeSampleCartQuantity();
        const updatedGuestSessionData = this.getDefaultSessionData();
        updatedGuestSessionData.free_samples_remaining = Math.max(0, FREE_SAMPLE_LIMIT - updatedCartQuantity);
        updatedGuestSessionData.has_exceeded_limit = updatedCartQuantity >= FREE_SAMPLE_LIMIT;
        console.log('[FREE-SAMPLE]   Guest button update: cart now has', updatedCartQuantity, 'free samples, remaining:', updatedGuestSessionData.free_samples_remaining);
        this.updateAllButtons(updatedGuestSessionData, false);
        // Update cached guest session data for page navigations
        if (window.freeSampleGuestSessionData) {
          window.freeSampleGuestSessionData.data = updatedGuestSessionData;
          window.freeSampleGuestSessionData.timestamp = Date.now();
        }
        // Record timestamp to debounce cartUpdate events
        this.guestButtonUpdateTimestamp = Date.now();
      }

      if (email) {
        await this.recordSampleOrderImmediately(email, shippingZip, sampleSku || parentSku, variantId, isPaid, freeSamplesRemaining, pricingModel);
        await this.refreshSessionState(email, sessionData, isPaid);
      }

      // Show success feedback
      this.showSuccessMessage();
      
      // Dispatch custom event for other parts of the page to listen to
      this.dispatchEvent(new CustomEvent('sample-added', {
        detail: { item: data.items[0] },
        bubbles: true,
        composed: true
      }));

    } catch (error) {
      const formData = new FormData(this.form);
      const email = formData.get('email') || window.Shopify?.customer?.email || '';
      if (email) {
        const previousSessionData = preSubmitSessionData || this.getCachedSessionData(email);
        if (previousSessionData) {
          this.setCachedSessionData(email, previousSessionData, true);
        }
      }
      console.error('Error adding sample to cart:', error);
      this.showErrorMessage(error.message);
    } finally {
      // Clear loading state
      this.button.removeAttribute('aria-busy');
      this.button.disabled = false;
    }
  }

  async validateSample(email, shippingZip, sku) {
    try {
      const response = await fetch(`${this.apiUrl}/api/sample-validate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email,
          shipping_zip: shippingZip,
          sample_items: [
            { sku, qty: 1 }
          ]
        })
      });

      if (!response.ok) {
        throw new Error('API validation failed');
      }

      return await response.json();
    } catch (error) {
      console.error('Sample validation error:', error);
      // If validation fails, allow the order to proceed (API might be down)
      return { allowed: true };
    }
  }

  async recordSampleOrder(email, shippingZip, sku, variantId) {
    try {
      await fetch(`${this.apiUrl}/api/sample-record`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email,
          shipping_zip: shippingZip,
          sample_skus: [sku],
          created_at: new Date().toISOString(),
          source: 'shopify_checkout'
        })
      });
    } catch (error) {
      console.error('Failed to record sample order:', error);
      // Don't throw - this is a logging operation
    }
  }

  async recordSampleOrderImmediately(email, shippingZip, sku, variantId, isPaid, freeSamplesRemaining, pricingModel) {
    try {
      const eventId = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${variantId}-${sku}`;

      // Call Vercel endpoint to record the sample order immediately
      // This happens before checkout, allowing us to track when button was clicked
      const response = await fetch(`${this.apiUrl}/api/sample-record`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email,
          shipping_zip: shippingZip,
          sample_skus: [sku],
          variant_id: variantId,
          event_id: eventId,
          was_paid: isPaid,
          free_samples_remaining: freeSamplesRemaining,
          pricing_model: pricingModel,
          created_at: new Date().toISOString(),
          source: 'button_click'
        })
      });

      if (!response.ok) {
        console.warn('Failed to record sample order:', response.status);
        // Don't throw - allow cart add to continue even if recording fails
      }

      return await response.json();
    } catch (error) {
      console.error('Error recording sample order immediately:', error);
      // Don't throw - this is a logging operation and shouldn't block the purchase
    }
  }

  showSuccessMessage() {
    const originalText = this.button.querySelector('.free-sample-button__text').textContent;
    const successText = window.freeSampleMessages?.success || 'Sample Added!';
    
    this.button.querySelector('.free-sample-button__text').textContent = successText;
    this.button.classList.add('free-sample-button--success');
    
    setTimeout(() => {
      this.button.querySelector('.free-sample-button__text').textContent = originalText;
      this.button.classList.remove('free-sample-button--success');
    }, 3000);
  }

  showErrorMessage(customMessage) {
    const originalText = this.button.querySelector('.free-sample-button__text').textContent;
    const errorText = customMessage || window.freeSampleMessages?.error || 'Error Adding Sample';
    
    this.button.querySelector('.free-sample-button__text').textContent = errorText;
    this.button.classList.add('free-sample-button--error');
    
    setTimeout(() => {
      this.button.querySelector('.free-sample-button__text').textContent = originalText;
      this.button.classList.remove('free-sample-button--error');
    }, 3000);
  }
}

// Register the custom element
if (!customElements.get('free-sample-button')) {
  customElements.define('free-sample-button', FreeSampleButton);
  console.log('[FREE-SAMPLE] Custom element <free-sample-button> registered');
} else {
  console.log('[FREE-SAMPLE] Custom element <free-sample-button> already registered');
}
